import type { NavigateFunction } from 'react-router-dom'
import { TaskService, type Task } from '../services/taskService'

/**
 * "Plan it" — the one place a task gets a slot in time (task review
 * 2026-09-04 §P1-6). Four destinations, each already backed by an existing
 * plane; this only routes:
 *   today   → Today's focus pin (+ committed / today)
 *   pick    → the schedule page with this task highlighted to drag
 *   place   → review-schedule (assistant places it; Flexible / HOLD)
 *   backlog → user_tier backlog + target_window someday
 */
export type PlanTarget = 'today' | 'pick' | 'place' | 'backlog'

export type PlanTask = Pick<Task, 'id' | 'title' | 'priority' | 'estimated_duration' | 'user_tier' | 'target_window' | 'due_date'>

export async function planTask(
  task: PlanTask,
  target: PlanTarget,
  navigate: NavigateFunction,
): Promise<{ title: string; description?: string } | null> {
  const service = new TaskService()
  switch (target) {
    case 'today': {
      const res = await fetch('/api/orchestrator/pin-task', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: task.id, pinned: true }),
      })
      const body = await res.json().catch(() => ({} as Record<string, unknown>))
      if (!res.ok || body.success === false) {
        throw new Error((body.error as string) || (body.message as string) || 'Today would not take it')
      }
      await service.updateTask(task.id, { user_tier: 'committed', target_window: 'today' })
      return { title: 'On Today', description: task.title }
    }
    case 'pick':
      navigate('/schedule', { state: { focusTaskId: task.id } })
      return null
    case 'place':
      navigate('/review-schedule', {
        state: {
          items: [{
            id: task.id,
            type: 'task' as const,
            title: task.title,
            priority: task.priority || 'medium',
            estimated_duration: task.estimated_duration || 30,
            due_date: task.due_date || undefined,
          }],
        },
      })
      return null
    case 'backlog':
      await service.updateTask(task.id, { user_tier: 'backlog', target_window: 'someday' })
      return { title: 'Parked in the backlog', description: 'It stays on your list; nothing will nag you about it.' }
  }
}

